import { Image, ImageStyle, Platform, View } from 'react-native';
import React, { useState } from 'react';

import { AppEnv } from '@components/AppEnv';
import { Collapsable } from '@components/Collapsable';
import { LogoPlusElmenus } from '@components/Logo';
import { Typography } from '@components/Typography';
import { useAuthStore } from '@stores/auth';
import { useI18nValue } from '@hooks/useI18nValue';
import { useLookupsStore } from '@stores/lookups';
import { useTranslation } from '@providers/I18n';
import { Direction, useDirection } from '@providers/Direction';
import { makeStyles, useTheme } from '@providers/Theme';
import { useAcceptedOrdersStore, useNewOrdersStore } from '@stores';
import { useNavigation, useSyncNativeDrawerStatus } from '@hooks';

import { SidebarButton } from './SidebarButton';

type SidebarStylesProps = {
  direction: Direction;
};

const useStyles = makeStyles<SidebarStylesProps>((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    height: '100%',
    width: '100%',
    overflowX: 'auto',
    overflowY: 'auto',
    backgroundColor: '#fff',
    ...theme.mixins.padding(2),
    ...theme.mixins.if(
      Platform.OS === 'web' && theme.screen.width < theme.breakpoints.md,
      {
        paddingTop: 80,
      },
    ),
    on: {
      md: {
        borderStyle: 'solid',
        borderRightWidth: 1,
        borderRightColor: theme.palette.grey[400],
      },
    },
  },
  logo: () => ({
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    alignItems: 'center',
  }),
  group: {
    paddingLeft: theme.spacing(6),
    width: '100%',
  },
  gap: {
    flexGrow: 1,
  },
  info: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    borderTopWidth: 2,
    borderTopColor: theme.palette.grey[300],
    paddingTop: theme.spacing(2),
    marginTop: theme.spacing(2),
    paddingLeft: theme.spacing(1),
  },
  avatar: {
    width: 40,
    height: 40,
    maxWidth: 40,
    maxHeight: 40,
    flex: 2,
    borderRadius: 50,
  },
  content: (props) => ({
    flex: 5,
    marginHorizontal: 10,
    marginTop: 5,
    alignItems: props.direction === 'rtl' ? 'flex-end' : 'flex-start',
    marginBottom: theme.spacing(Platform.OS === 'web' ? 0 : 3), // to fix issue in tablet & mobile
  }),
  rest: (props) => ({
    color: theme.palette.cool[700],
    lineHeight: props.direction === 'rtl' ? 18 : 14,
  }),
  agent: (props) => ({
    color: theme.palette.grey[700],
    lineHeight: props.direction === 'rtl' ? 18 : 14,
  }),
  appEnv: {
    marginLeft: theme.spacing(1),
  },
}));

type SidebarProps = {
  testID?: string;
};

export const Sidebar: React.FC<SidebarProps> = ({ testID }) => {
  const { logout } = useAuthStore();
  const navigation = useNavigation();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [ordersItemExpanded, setOrdersItemExpanded] = useState(true);

  const { direction } = useDirection();
  const styles = useStyles({ direction });
  // watches the native drawer state and syncs its state with drawerStateStore
  useSyncNativeDrawerStatus();

  const lookups = useLookupsStore();
  const { orders: newOrders } = useNewOrdersStore();
  const { orders: acceptedOrders } = useAcceptedOrdersStore();

  const restaurantName = useI18nValue(lookups.value?.restaurantName);

  if (!lookups) {
    return null;
  }

  return (
    <View style={styles.root} testID={testID}>
      {theme.screen.width >= theme.breakpoints.md && (
        <View style={styles.logo}>
          <LogoPlusElmenus testID="logoPluselmenus" />
          <AppEnv />
        </View>
      )}

      <SidebarButton
        label={t('sidebar.orders', 'Orders')}
        onPress={() => setOrdersItemExpanded((v) => !v)}
        startIcon="order"
        endIcon={ordersItemExpanded ? 'arrow-up' : 'arrow-down'}
        testID="orders"
        accessibilityLabel="items"
      />

      <Collapsable open={ordersItemExpanded} testID="collapse">
        <View style={styles.group} testID="collapse-content">
          <SidebarButton
            label={t('sidebar.new', 'New')}
            onPress={() => navigation.navigate('NewOrders')}
            activeRouteNames={{
              NewOrder: true,
              NewOrders: true,
            }}
            testID="newOrders"
            className="collapse-items"
            badgeLabel={newOrders.length}
          />

          <SidebarButton
            label={t('sidebar.accepted', 'Accepted')}
            onPress={() => navigation.navigate('AcceptedOrders')}
            activeRouteNames={{
              AcceptedOrders: true,
            }}
            testID="accepted"
            className="collapse-items"
            badgeLabel={acceptedOrders.length}
          />

          <SidebarButton
            label={t('sidebar.history', 'History')}
            onPress={() => navigation.navigate('OrdersHistory')}
            activeRouteNames={{
              OrdersHistory: true,
            }}
            testID="history"
            className="collapse-items"
          />
        </View>
      </Collapsable>

      <SidebarButton
        label={t('sidebar.menu', 'Menu')}
        onPress={() => navigation.navigate('Menu')}
        activeRouteNames={{
          Menu: true,
        }}
        startIcon="menu"
        testID="menu"
        accessibilityLabel="items"
      />

      <View style={styles.gap} />

      <SidebarButton
        label={t('sidebar.language', {
          defaultValue: 'عربي',
        })}
        onPress={() => {
          i18n.changeLanguage(i18n.language === 'en' ? 'ar' : 'en');
        }}
        startIcon={i18n.language === 'en' ? 'ar' : 'en'}
        TypographyProps={{
          locale: i18n.language === 'en' ? 'ar' : 'en',
        }}
        testID="switcher"
        accessibilityLabel="items"
      />
      <SidebarButton
        label={t('sidebar.signOut', 'Sign out')}
        onPress={() => {
          navigation.closeDrawer();
          logout();
        }}
        startIcon="logout"
        testID="logout"
        accessibilityLabel="items"
      />
      <View style={styles.info} testID="info">
        {lookups.value?.restaurantPhotoUrl && (
          <Image
            testID="rest-logo"
            style={styles.avatar as ImageStyle}
            source={{
              uri: lookups.value?.restaurantPhotoUrl,
            }}
          />
        )}
        <View style={styles.content}>
          {restaurantName && (
            <Typography
              weight="semiBold"
              size="sm"
              style={styles.rest}
              testID="rest-name">
              {restaurantName}
            </Typography>
          )}
          {lookups.value?.userCode && (
            <Typography
              weight="regular"
              size="sm"
              style={styles.agent}
              testID="agent-name">
              {lookups.value?.userCode}
            </Typography>
          )}
        </View>
      </View>
    </View>
  );
};
